import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaceDetector,
  FilesetResolver,
  type FaceDetectorResult,
} from "@mediapipe/tasks-vision";
import visionWasmLoaderUrl from "@mediapipe/tasks-vision/vision_wasm_internal.js?url";
import visionWasmUrl from "@mediapipe/tasks-vision/vision_wasm_internal.wasm?url";
import visionWasmNoSimdLoaderUrl from "@mediapipe/tasks-vision/vision_wasm_nosimd_internal.js?url";
import visionWasmNoSimdUrl from "@mediapipe/tasks-vision/vision_wasm_nosimd_internal.wasm?url";

const MODEL_URL = "/mediapipe/models/blaze_face_short_range.tflite";
const MODEL_INITIALIZATION_ATTEMPTS = 3;
const MODEL_RETRY_BASE_DELAY_MS = 1_000;

// Minimum face bounding-box width as a fraction of the frame width.
// Tuned so a typical adult face at ~1m from an iPad front camera passes,
// while faces in the background (further than ~1.5m) are rejected.
const MIN_FACE_WIDTH_RATIO = 0.07;

const CAMERA_ACQUIRE_TIMEOUT_MS = 5000;
const CAMERA_STARTUP_TIMEOUT_MS = 5000;
const CAPTURE_RETRY_TIMEOUT_MS = 2500;
const CAPTURE_RETRY_INTERVAL_MS = 120;

export type GateStatus =
  | "idle"
  | "initializing"
  | "ready"
  | "detecting"
  | "error";

export type FaceGateStage =
  | "model"
  | "camera_acquire"
  | "video_start"
  | "frame_capture"
  | "inference";

export type FaceGateTechnicalReason =
  | "timeout"
  | "permission_denied"
  | "camera_unavailable"
  | "play_rejected"
  | "frame_unavailable"
  | "model_unavailable"
  | "inference_failed"
  | "unexpected";

export class FaceGateError extends Error {
  readonly name = "FaceGateError";

  constructor(
    message: string,
    readonly stage: FaceGateStage,
    readonly reason: FaceGateTechnicalReason,
    readonly retryable: boolean,
    readonly durationMs: number,
    readonly diagnostics: {
      readyState?: number;
      videoWidth?: number;
      videoHeight?: number;
    } = {},
  ) {
    super(message);
  }
}

export type DetectResult =
  | {
      passed: true;
      ratio: number;
      faceCount: number;
      frameWidth: number;
      frameHeight: number;
      brightness: number | null;
      capture: FaceCapture;
    }
  | {
      passed: false;
      ratio: number;
      reason: "no_face" | "too_small";
      faceCount: number;
      frameWidth: number;
      frameHeight: number;
      brightness: number | null;
      capture: FaceCapture;
    };

export type FaceBox = {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  score: number;
};

export type FaceCapture = {
  imageDataUrl: string | null;
  frameWidth: number;
  frameHeight: number;
  boxes: FaceBox[];
  maxWidthRatio: number;
  passed: boolean;
  reason: "no_face" | "too_small" | null;
  capturedAt: number | null;
  brightness: number | null;
};

const EMPTY_CAPTURE: FaceCapture = {
  imageDataUrl: null,
  frameWidth: 0,
  frameHeight: 0,
  boxes: [],
  maxWidthRatio: 0,
  passed: false,
  reason: null,
  capturedAt: null,
  brightness: null,
};

export function useFaceGate() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const detectorRef = useRef<FaceDetector | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const activeAttemptRef = useRef(0);
  const mountedRef = useRef(true);
  const inFlightRef = useRef<Promise<DetectResult> | null>(null);

  const [status, setStatus] = useState<GateStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastCapture, setLastCapture] = useState<FaceCapture>(EMPTY_CAPTURE);

  // On mount: preload MediaPipe model only. The camera stays off until detect.
  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    mountedRef.current = true;

    async function init() {
      setStatus("initializing");
      setErrorMessage(null);
      for (let attempt = 0; attempt < MODEL_INITIALIZATION_ATTEMPTS; attempt++) {
        try {
          const simdSupported = await FilesetResolver.isSimdSupported();
          const fileset = {
            wasmLoaderPath: simdSupported
              ? visionWasmLoaderUrl
              : visionWasmNoSimdLoaderUrl,
            wasmBinaryPath: simdSupported
              ? visionWasmUrl
              : visionWasmNoSimdUrl,
          };
          if (cancelled) return;

          let detector: FaceDetector;
          try {
            detector = await FaceDetector.createFromOptions(fileset, {
              baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
              runningMode: "IMAGE",
              minDetectionConfidence: 0.5,
            });
          } catch {
            detector = await FaceDetector.createFromOptions(fileset, {
              baseOptions: { modelAssetPath: MODEL_URL, delegate: "CPU" },
              runningMode: "IMAGE",
              minDetectionConfidence: 0.5,
            });
          }

          if (cancelled) {
            detector.close();
            return;
          }

          detectorRef.current = detector;
          setStatus("ready");
          return;
        } catch (error) {
          if (cancelled) return;
          if (attempt + 1 < MODEL_INITIALIZATION_ATTEMPTS) {
            await delay(MODEL_RETRY_BASE_DELAY_MS * 2 ** attempt);
            continue;
          }
          const message =
            error instanceof Error ? error.message : "Model failed to load.";
          setErrorMessage(message);
          setStatus("error");
        }
      }
    }

    init();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      activeAttemptRef.current += 1;
      releaseCamera(activeStreamRef.current, video);
      activeStreamRef.current = null;
      if (detectorRef.current) {
        detectorRef.current.close();
        detectorRef.current = null;
      }
    };
  }, []);

  const detectOnce = useCallback((): Promise<DetectResult> => {
    if (inFlightRef.current) return inFlightRef.current;

    const attempt = activeAttemptRef.current + 1;
    activeAttemptRef.current = attempt;
    const startedAt = performance.now();
    const detector = detectorRef.current;
    const video = videoRef.current;

    const operation = (async () => {
      if (!detector || !video) {
        throw new FaceGateError(
          "Face detector is not ready.",
          "model",
          "model_unavailable",
          false,
          performance.now() - startedAt,
        );
      }

      setStatus("detecting");
      setErrorMessage(null);

      try {
        const stream = await acquireCamera(startedAt);
        if (!mountedRef.current || activeAttemptRef.current !== attempt) {
          releaseCamera(stream, video);
          throw new FaceGateError(
            "Face detection was cancelled.",
            "camera_acquire",
            "unexpected",
            true,
            performance.now() - startedAt,
          );
        }
        activeStreamRef.current = stream;
        video.srcObject = stream;

        await startVideo(video, startedAt);
        const frame = await captureUsableVideoFrame(video, startedAt);

        let result: FaceDetectorResult;
        try {
          result = detector.detect(frame.canvas);
        } catch {
          throw new FaceGateError(
            "Face detection failed.",
            "inference",
            "inference_failed",
            true,
            performance.now() - startedAt,
            videoDiagnostics(video),
          );
        }

        const nextCapture = toFaceCapture(
          frame.imageDataUrl,
          result,
          video.videoWidth,
          video.videoHeight,
          frame.brightness,
        );
        const nextResult = toDetectResult(nextCapture);
        if (mountedRef.current && activeAttemptRef.current === attempt) {
          setLastCapture({
            ...nextCapture,
            passed: nextResult.passed,
            reason: nextResult.passed ? null : nextResult.reason,
          });
        }
        return nextResult;
      } catch (error) {
        const faceGateError = toFaceGateError(error, startedAt, video);
        if (mountedRef.current && activeAttemptRef.current === attempt) {
          setErrorMessage(faceGateError.message);
        }
        throw faceGateError;
      } finally {
        releaseCamera(activeStreamRef.current, video);
        activeStreamRef.current = null;
        if (mountedRef.current && activeAttemptRef.current === attempt) {
          setStatus(detectorRef.current ? "ready" : "error");
        }
      }
    })();

    inFlightRef.current = operation;
    void operation.finally(() => {
      if (inFlightRef.current === operation) inFlightRef.current = null;
    }).catch(() => undefined);
    return operation;
  }, []);

  const resetCapture = useCallback(() => {
    setLastCapture(EMPTY_CAPTURE);
  }, []);

  return {
    videoRef,
    status,
    errorMessage,
    lastCapture,
    detectOnce,
    resetCapture,
  };
}

function toFaceCapture(
  imageDataUrl: string,
  result: FaceDetectorResult,
  frameWidth: number,
  frameHeight: number,
  brightness: number,
): FaceCapture {
  if (!frameWidth || !frameHeight) {
    return { ...EMPTY_CAPTURE, imageDataUrl, brightness };
  }

  let maxWidthRatio = 0;
  const boxes: FaceBox[] = [];

  result.detections.forEach((detection, index) => {
    const boundingBox = detection.boundingBox;
    if (!boundingBox) return;

    const widthRatio = boundingBox.width / frameWidth;
    if (widthRatio > maxWidthRatio) maxWidthRatio = widthRatio;

    boxes.push({
      id: index,
      x: boundingBox.originX,
      y: boundingBox.originY,
      width: boundingBox.width,
      height: boundingBox.height,
      score: detection.categories[0]?.score ?? 0,
    });
  });

  return {
    imageDataUrl,
    frameWidth,
    frameHeight,
    boxes,
    maxWidthRatio,
    passed: maxWidthRatio >= MIN_FACE_WIDTH_RATIO,
    reason: null,
    capturedAt: Date.now(),
    brightness,
  };
}

function toDetectResult(capture: FaceCapture): DetectResult {
  const diagnostics = {
    faceCount: capture.boxes.length,
    frameWidth: capture.frameWidth,
    frameHeight: capture.frameHeight,
    brightness: capture.brightness,
  };
  if (capture.boxes.length === 0) {
    return {
      passed: false,
      ratio: 0,
      reason: "no_face",
      ...diagnostics,
      capture,
    };
  }
  if (!capture.passed) {
    return {
      passed: false,
      ratio: capture.maxWidthRatio,
      reason: "too_small",
      ...diagnostics,
      capture,
    };
  }
  return {
    passed: true,
    ratio: capture.maxWidthRatio,
    ...diagnostics,
    capture,
  };
}

async function captureUsableVideoFrame(
  video: HTMLVideoElement,
  detectionStartedAt: number,
): Promise<{
  canvas: HTMLCanvasElement;
  imageDataUrl: string;
  brightness: number;
}> {
  const startedAt = performance.now();
  let lastFrame = captureVideoFrame(video);

  while (
    !isUsableFrame(lastFrame.brightness) &&
    performance.now() - startedAt < CAPTURE_RETRY_TIMEOUT_MS
  ) {
    await waitForNextVideoFrame(video);
    await delay(CAPTURE_RETRY_INTERVAL_MS);
    lastFrame = captureVideoFrame(video);
  }

  if (!isUsableFrame(lastFrame.brightness)) {
    throw new FaceGateError(
      "Camera did not produce a usable frame.",
      "frame_capture",
      "frame_unavailable",
      true,
      performance.now() - detectionStartedAt,
      videoDiagnostics(video),
    );
  }

  return lastFrame;
}

function captureVideoFrame(video: HTMLVideoElement): {
  canvas: HTMLCanvasElement;
  imageDataUrl: string;
  brightness: number;
} {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is unavailable.");
  }

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return {
    canvas,
    imageDataUrl: canvas.toDataURL("image/jpeg", 0.92),
    brightness: getAverageBrightness(context, canvas.width, canvas.height),
  };
}

function getAverageBrightness(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): number {
  const sampleWidth = Math.min(width, 80);
  const sampleHeight = Math.min(height, 60);
  const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
  let total = 0;

  for (let i = 0; i < imageData.length; i += 4) {
    total += (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
  }

  return total / (imageData.length / 4);
}

function isUsableFrame(brightness: number): boolean {
  return brightness > 8;
}

function waitForNextVideoFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, CAPTURE_RETRY_INTERVAL_MS);

    if ("requestVideoFrameCallback" in video) {
      video.requestVideoFrameCallback(finish);
      return;
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(finish);
    });
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function waitForFirstFrame(
  video: HTMLVideoElement,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (video.readyState >= 2 && video.videoWidth > 0) {
      resolve();
      return;
    }
    const onReady = () => {
      if (video.videoWidth > 0) {
        cleanup();
        resolve();
      }
    };
    const onError = () => {
      cleanup();
      reject(new Error("Video failed to load."));
    };
    const cleanup = () => {
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("playing", onReady);
      video.removeEventListener("error", onError);
      signal.removeEventListener("abort", cleanup);
      window.clearTimeout(timer);
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("Camera startup timed out."));
    }, CAMERA_STARTUP_TIMEOUT_MS);
    video.addEventListener("loadeddata", onReady);
    video.addEventListener("canplay", onReady);
    video.addEventListener("playing", onReady);
    video.addEventListener("error", onError);
    signal.addEventListener("abort", cleanup, { once: true });
  });
}

async function acquireCamera(detectionStartedAt: number): Promise<MediaStream> {
  let timedOut = false;
  let timeoutId: number | undefined;
  let request: Promise<MediaStream>;
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Camera API is unavailable.");
    }
    request = navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "user",
        width: { ideal: 640 },
        height: { ideal: 480 },
      },
      audio: false,
    });
  } catch (error) {
    throw cameraAcquisitionError(error, detectionStartedAt);
  }
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      timedOut = true;
      reject(
        new FaceGateError(
          "Camera access timed out.",
          "camera_acquire",
          "timeout",
          true,
          performance.now() - detectionStartedAt,
        ),
      );
    }, CAMERA_ACQUIRE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([request, timeout]);
  } catch (error) {
    if (timedOut) {
      void request.then(
        (lateStream) => releaseCamera(lateStream, null),
        () => undefined,
      );
    }
    if (error instanceof FaceGateError) throw error;
    throw cameraAcquisitionError(error, detectionStartedAt);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

async function startVideo(
  video: HTMLVideoElement,
  detectionStartedAt: number,
): Promise<void> {
  const controller = new AbortController();
  const firstFrame = waitForFirstFrame(video, controller.signal).catch((error) => {
    throw new FaceGateError(
      error instanceof Error ? error.message : "Camera startup failed.",
      "video_start",
      error instanceof Error && error.message.includes("timed out")
        ? "timeout"
        : "play_rejected",
      true,
      performance.now() - detectionStartedAt,
      videoDiagnostics(video),
    );
  });
  let play: Promise<void>;
  try {
    play = video.play();
  } catch {
    controller.abort();
    throw new FaceGateError(
      "Camera video could not start.",
      "video_start",
      "play_rejected",
      true,
      performance.now() - detectionStartedAt,
      videoDiagnostics(video),
    );
  }
  const playRejected = play.then(
    () => new Promise<never>(() => undefined),
    () => {
      throw new FaceGateError(
        "Camera video could not start.",
        "video_start",
        "play_rejected",
        true,
        performance.now() - detectionStartedAt,
        videoDiagnostics(video),
      );
    },
  );
  try {
    await Promise.race([firstFrame, playRejected]);
  } finally {
    controller.abort();
  }
}

function isPermissionDenied(error: unknown): boolean {
  return (
    Boolean(error) &&
    typeof error === "object" &&
    ((error as { name?: string }).name === "NotAllowedError" ||
      (error as { name?: string }).name === "SecurityError")
  );
}

function cameraAcquisitionError(
  error: unknown,
  detectionStartedAt: number,
): FaceGateError {
  if (isPermissionDenied(error)) {
    return new FaceGateError(
      "Camera permission was denied.",
      "camera_acquire",
      "permission_denied",
      false,
      performance.now() - detectionStartedAt,
    );
  }
  return new FaceGateError(
    "Camera is unavailable.",
    "camera_acquire",
    "camera_unavailable",
    true,
    performance.now() - detectionStartedAt,
  );
}

function toFaceGateError(
  error: unknown,
  detectionStartedAt: number,
  video: HTMLVideoElement,
): FaceGateError {
  if (error instanceof FaceGateError) return error;
  return new FaceGateError(
    error instanceof Error ? error.message : "Face detection failed.",
    "frame_capture",
    "unexpected",
    true,
    performance.now() - detectionStartedAt,
    videoDiagnostics(video),
  );
}

function videoDiagnostics(video: HTMLVideoElement) {
  return {
    readyState: video.readyState,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
  };
}

function releaseCamera(
  stream: MediaStream | null,
  video: HTMLVideoElement | null,
) {
  stream?.getTracks().forEach((track) => track.stop());
  if (!video) return;
  video.pause();
  video.srcObject = null;
}
